/**
 * Chart tile body (S3) — ChartRenderer + useChartData from the charts
 * feature, fed by `useTileResult` (the single data hook). Flicker-free by
 * construction: previous data stays rendered through refetches AND failures;
 * Recharts animation runs only on the first data mount. Designed states live
 * in QueryTileStates (shared with S4/S5 tile views).
 *
 * S11: mark clicks toggle dashboard cross-filters; dblclick drills through
 * to a query tab with the mark's predicate merged over the active filters.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { getEngineForConnection } from '../../../../connections/utils/connectionLookup';
import { ChartRenderer, type ChartRendererHandle } from '../../../charts/components/ChartRenderer';
import { useChartData } from '../../../charts/hooks/useChartData';
import { bindingIdentityForMarkEvent } from '../../../charts/utils/markEventBindingIdentity';
import type { ChartMarkEvent } from '../../../charts/types/chartTypes';
import type { ChartTile } from '../../types/dashboardTypes';
import { useTileResult } from '../../hooks/useTileResult';
import { markEventToFilters, relevantFiltersForTile } from '../../services/crossFilter';
import { registerChartSvgSource, unregisterChartSvgSource } from '../../services/chartSvgRegistry';
import { tileEmptyText } from './tileEmptyText';
import { openDrillThrough } from '../../services/tileNavigation';
import {
  selectActiveFilters,
  useDashboardCrossFilterStore,
} from '../../store/dashboardCrossFilterStore';
import { tileRuntimeKey } from '../../store/dashboardRuntimeStore';
import { QueryTileStates } from './QueryTileStates';

export function ChartTileView({ dashboardId, tile }: { dashboardId: string; tile: ChartTile }) {
  const state = useTileResult(dashboardId, tile);
  const { chartData, hasData, resolvedConfig, columnInfos } = useChartData(
    state.result?.selected,
    tile.chartConfig,
  );
  const { track } = useAnalytics();

  // Shaping/rendering reads rows by the RESOLVED key (`resolvedConfig`,
  // above), so a mark event's `field`/`seriesField` come out key-shaped too.
  // The cross-filter/drill-through layer matches on the persisted binding
  // identity instead — a real SQL column NAME, never the internal row key
  // (`markEventToFilters`/`deriveVqeFilterBindings` — see
  // `markEventBindingIdentity.ts`) — so every mark event is translated back
  // before it reaches `markEventToFilters`/`openDrillThrough` (PR review, P2).
  // `null` = no identity can be established (an ambiguous column name with
  // no binding naming its key): the mark is ignored rather than filtering or
  // drilling on an internal `col_<i>` key.
  const toBindingEvent = useCallback(
    (event: ChartMarkEvent) =>
      bindingIdentityForMarkEvent(
        event,
        tile.chartConfig,
        resolvedConfig ?? tile.chartConfig,
        columnInfos,
        tile.filterBindings,
      ),
    [tile.chartConfig, resolvedConfig, columnInfos, tile.filterBindings],
  );

  // D8: expose the rendered chart SVG to the tile kebab's image-export item
  // (rendered in a different subtree). The accessor reads chartRef lazily, so
  // it stays correct as the chart mounts/unmounts with data.
  const chartRef = useRef<ChartRendererHandle>(null);
  useEffect(() => {
    const key = tileRuntimeKey(dashboardId, tile.id);
    // Same closure reference passed to both calls — unregister removes THIS
    // accessor from the per-key stack, not just any accessor at this key
    // (the grid body and the expand-dialog overlay can both be mounted for
    // the same tile at once, each registering its own).
    const getSvgContent = () => chartRef.current?.getSvgContent() ?? null;
    registerChartSvgSource(key, getSvgContent);
    return () => unregisterChartSvgSource(key, getSvgContent);
  }, [dashboardId, tile.id]);

  // Animate only the first mount with data — a refetch swap must not replay
  // the entrance animation (UI Quality §3).
  const hasAnimatedRef = useRef(false);
  const animate = hasData && !hasAnimatedRef.current;
  useEffect(() => {
    if (hasData) hasAnimatedRef.current = true;
  }, [hasData]);

  const handleMarkClick = useCallback(
    (event: ChartMarkEvent) => {
      const bound = toBindingEvent(event);
      if (!bound) return;
      const incoming = markEventToFilters(tile, bound);
      const store = useDashboardCrossFilterStore.getState();
      store.applyMark(dashboardId, incoming);
      // Toggle semantics: fire analytics only when the mark APPLIED filters
      // (a repeat click that cleared them is not an apply).
      const after = selectActiveFilters(dashboardId)(useDashboardCrossFilterStore.getState());
      const applied = incoming.every((inc) =>
        after.some(
          (a) =>
            a.dashboardField.toLowerCase() === inc.dashboardField.toLowerCase() &&
            a.value === inc.value,
        ),
      );
      if (applied) {
        track('feature:dashboard_crossfilter', { engine: getEngineForConnection(tile.connectionId) });
      }
    },
    [dashboardId, tile, toBindingEvent, track],
  );

  const handleMarkDblClick = useCallback(
    (event: ChartMarkEvent) => {
      const bound = toBindingEvent(event);
      if (!bound) return;
      const engine = getEngineForConnection(tile.connectionId);
      const active = selectActiveFilters(dashboardId)(useDashboardCrossFilterStore.getState());
      const markFilters = markEventToFilters(tile, bound);
      // Drill = active filters this tile honors + the clicked mark; the mark
      // wins on field collision (you drill into what you clicked).
      const markFields = new Set(markFilters.map((f) => f.dashboardField.toLowerCase()));
      const merged = [
        ...relevantFiltersForTile(tile, active, engine).filter(
          (f) => !markFields.has(f.dashboardField.toLowerCase()),
        ),
        ...markFilters,
      ];
      openDrillThrough(tile, merged, engine);
      track('feature:dashboard_drill_through', { engine });
    },
    [dashboardId, tile, toBindingEvent, track],
  );

  return (
    <QueryTileStates state={state}>
      {hasData ? (
        <div className="h-full min-h-0 w-full px-2 pb-2" data-testid="chart-tile-body">
          <ChartRenderer
            ref={chartRef}
            data={chartData}
            config={resolvedConfig ?? tile.chartConfig}
            height="100%"
            className="h-full"
            isAnimationActive={animate}
            resizeDebounceMs={150}
            referenceLines={tile.referenceLines}
            onMarkClick={handleMarkClick}
            onMarkDblClick={handleMarkDblClick}
          />
        </div>
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
          {tileEmptyText(tile, 'No data to display')}
        </div>
      )}
    </QueryTileStates>
  );
}
